import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';

import { buildClassNames } from '../util/utils';
import { renderableType } from '../proptypes/prop-types';
import './modal.scss';

const modalClassName = 'modal';
const backdropClassName = 'modal__backdrop';
const contentClassName = 'modal__content';
const contentContainerClassName = 'modal__contentContainer';
const footerClassName = 'modal__footer';
const footerContentClassName = 'modal__footerContent';
const headerClassName = 'modal__header';
const headerContentClassName = 'modal__headerContent';
const headerCloseIconClassName = 'modal__headerCloseIcon';

/* TODO: Need to handle the container in a cleaner way.
*  Also need to make the other elements in the container
*  aria-hidden and remove scrolling.
*/

/** A component for displaying content over a backdrop that
 * prevents interaction with the content behind it. */
export class Modal extends Component {
  static propTypes = {
    /** @ignore */
    children: renderableType.isRequired,
    /** @ignore */
    className: PropTypes.string,
    /** The element where the modal will be rendered */
    container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    /** Determines whether clicking the backdrop will close the modal */
    disabledBackdropClick: PropTypes.bool,
    /** Content rendered in the footer of the modal */
    footer: renderableType,
    /** Content rendered in the header of the modal */
    header: renderableType,
    /** Function executed when the backdrop is clicked */
    onBackdropClick: PropTypes.func,
    /** Function executed when the modal is closed */
    onClose: PropTypes.func,
    /** Indicates whether the modal is visible */
    open: PropTypes.bool,
  };

  static defaultProps = {
    container: document.body,
    disabledBackdropClick: false,
    open: false,
  };

  componentDidMount() {
    if (this.props.open) this.toggleKeyboardListener(true);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open !== this.props.open) {
      if (this.props.open) {
        this.toggleKeyboardListener(true);
        return;
      }
      this.toggleKeyboardListener(false);
    }
  }

  componentWillUnmount() {
    if (!this.props.open) this.toggleKeyboardListener(false);
  }

  toggleKeyboardListener = (enabled) => {
    if (enabled) {
      document.addEventListener('keydown', this.handleDocumentKeyDown, true);
    } else {
      document.removeEventListener('keydown', this.handleDocumentKeyDown, true);
    }
  }

  handleDocumentKeyDown = (e) => {
  if (e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    const savedEvent = e;
        if (typeof this.props.onClose === 'function') {
        this.props.onClose(savedEvent);
        }
    }
  }

  handleBackdropClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const savedEvent = e;
    this.props.onBackdropClick(savedEvent);
  };

  handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const savedEvent = e;
    this.props.onClose(savedEvent);
  }

  render() {
    const {
      children,
      className,
      container,
      disabledBackdropClick,
      footer,
      header,
      onBackdropClick,
      onClose,
      open,
      ...other
    } = this.props;

    if (!open) return null;

    return (
      createPortal(
        <div className={buildClassNames(className, modalClassName)} {...other}>
          <div
            aria-hidden='true'
            className={backdropClassName}
            onClick={(!disabledBackdropClick && onBackdropClick)
              ? this.handleBackdropClick
              : undefined}
            onKeyDown={(!disabledBackdropClick && onClose) ? this.handleClose : undefined}
          />
          <div className={contentContainerClassName}>
            <div className={headerClassName}>
              <div className={headerContentClassName}>{header}</div>
              <div className={headerCloseIconClassName}>
                {onClose &&
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    onClick={onClose ? this.handleClose : undefined}
                  />
                }
              </div>
            </div>
            <div className={contentClassName}>{children}</div>
            {footer &&
              <div className={footerClassName}>
                <div className={footerContentClassName}>{footer}</div>
              </div>
            }
          </div>
        </div>,
        container,
      )
    );
  }
}
